import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  Signal,
  signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { forkJoin, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, tap } from 'rxjs/operators';

import {
  Mapper,
  MapperPipe,
  //MarkFormAsTouchedIfInvalid,
  MaskPipe,
  RegexHelper,
  StringHelper
} from 'tiime-expert-utils';
import {
  DialogBase,
  TableAnchorComponent,
  TiimeAlertModule,
  TiimeButtonModule,
  TiimeCardModule,
  TiimeDialogModule,
  TiimeIconModule,
  TiimeListModule,
  TiimeProgressSpinnerModule,
  TiimeSlideToggleContainerModule,
  TiimeSlideToggleModule,
  TiimeSnackbarService,
  TiimeStepperComponent,
  TiimeStepperModule,
  TiimeTableModule
} from 'tiime-material';

import { AccountingConfigApiService } from '@api-services/accounting-config-api.service';
import { BusinessUnitApiService } from '@api-services/business-unit-api.service';
import { BusinessUserApiService } from '@api-services/business-user-api.service';
import { CompanyApiService } from '@api-services/company-api.service';
import { UserApiService } from '@api-services/user-api.service';
import { UserInvitationApiService } from '@api-services/user-invitation-api.service';
import { SIRET_MASK } from '@constants/mask.constant';
import { TIIME_LABEL_PLAN_ID } from '@constants/tiime-label-plan.constant';
import { DIRECTOR_CAPACITIES } from '@constants/user-capacities.constant';
import { UserCapacity } from '@enums/user-capacity.enum';
import { ParametersStepLabelModeType } from '@forms/create-company-form';
import { ProspectApiService } from '@gi/api/services/prospect-api.service';
import { FormHelper } from '@helpers/form.helper';
import { remainSubscribedOnError } from '@helpers/rxjs.helper';
import { FormalityCodeNameEnum } from '@legal/legal-core/enums/formality-code-name.enum';
import { FormalitySupervisorEnum } from '@legal/legal-core/enums/formality-supervisor.enum';
import { FormalityTypeEnum } from '@legal/legal-core/enums/formality-type.enum';
import { CreationFormality } from '@legal/legal-core/models/creation-formality';
import { Formality } from '@legal/legal-core/models/formality';
import { FormalityBase } from '@legal/legal-core/models/formality-base';
import { CreationFormalityApiService } from '@legal/legal-core/services/creation-formality-api.service';
import { FormalityApiService } from '@legal/legal-core/services/formality-api.service';
import { AccountingConfig } from '@models/accounting-config';
import { BusinessUnit } from '@models/business-unit';
import { Company } from '@models/company';
import { LabelCategoryPlan } from '@models/label-category-plan';
import { LabelsPlan } from '@models/labels-plan';
import { PartnerConfig } from '@models/partner-config';
import { SearchUser } from '@models/search-user';
import { User } from '@models/user';
import { UserInvitation } from '@models/user-invitation';
import { UserInvitationConfiguration } from '@models/user-invitation-configuration';
import { AppStoreState } from '@modules/core/interfaces/app-store-state';
import { CreateCompany } from '@modules/core/models/create-company';
import {
  businessUserPartnerConfigSelector,
  businessUserPartnerIsNumbrSelector
} from '@modules/core/store/business-user/business-user-selector';
import { IncludesPipe } from '@modules/shared/pipes/includes/includes.pipe';
import { DuplicatedUsersContainerComponent } from '@shared-components/duplicated-users-container/duplicated-users-container.component';
import { ConfirmDialogData } from '@shared-dialogs/confirm-dialog/confirm-dialog-data';
import { ConfirmDialogModule } from '@shared-dialogs/confirm-dialog/confirm-dialog.module';
import { ConfirmDialogService } from '@shared-dialogs/confirm-dialog/confirm-dialog.service';
import { ExistingUserDialogModule } from '@shared-dialogs/existing-user-dialog/existing-user-dialog.module';
import { ExistingUserDialogService } from '@shared-dialogs/existing-user-dialog/existing-user-dialog.service';
import { CompanyInitializationFormComponent } from '@shared-forms/company-initialization-form/company-initialization-form.component';
import { CreateCompanyFormComponent } from '@shared-forms/create-company-form/create-company-form.component';
import { CreateUserFormComponent } from '@shared-forms/create-user-form/create-user-form.component';
import { InvitationFormComponent } from '@shared-forms/invitation-form/invitation-form.component';
import { CapitalizePipe } from '@shared-pipes/capitalize/capitalize/capitalize.pipe';
import { CompanyInfosLabelPipe } from '@shared-pipes/company-infos-label/company-infos-label.pipe';

import { CreateCompanyDialogData } from './create-company-dialog-data';
import { CreateCompanyState } from './create-company-state';
import { CreateCompanyStateFactory } from './create-company-state.factory';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-create-company-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TiimeProgressSpinnerModule,
    TiimeDialogModule,
    TiimeTableModule,
    TiimeStepperModule,
    TiimeButtonModule,
    TiimeCardModule,
    TiimeIconModule,
    TiimeAlertModule,
    TiimeSlideToggleContainerModule,
    TiimeSlideToggleModule,
    TiimeListModule,
    ExistingUserDialogModule,
    CompanyInitializationFormComponent,
    DuplicatedUsersContainerComponent,
    ConfirmDialogModule,
    InvitationFormComponent,
    CreateUserFormComponent,
    CreateCompanyFormComponent,
    CompanyInfosLabelPipe,
    CapitalizePipe,
    MaskPipe,
    MapperPipe,
    IncludesPipe
  ],
  templateUrl: './create-company-dialog.component.html',
  styleUrls: ['./create-company-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateCompanyDialogComponent
  extends DialogBase<CreateCompanyDialogComponent>
  implements OnInit, AfterViewInit
{
  readonly partnerConfig: Signal<PartnerConfig> = this.store.selectSignal(businessUserPartnerConfigSelector);

  readonly tableAnchorComponent: Signal<TableAnchorComponent> = viewChild<TableAnchorComponent>(TableAnchorComponent);
  readonly tiimeStepperComponent: Signal<TiimeStepperComponent> =
    viewChild<TiimeStepperComponent>(TiimeStepperComponent);
  readonly createUserFormComponent: Signal<CreateUserFormComponent | undefined> = viewChild<
    CreateUserFormComponent | undefined
  >(CreateUserFormComponent);
  readonly createCompanyFormComponent: Signal<CreateCompanyFormComponent | undefined> = viewChild<
    CreateCompanyFormComponent | undefined
  >(CreateCompanyFormComponent);

  readonly dialogData: Signal<CreateCompanyDialogData> = signal(inject(MAT_DIALOG_DATA)).asReadonly();
  readonly userExistingCompanies: WritableSignal<SearchUser[]> = signal<SearchUser[]>([]);
  readonly businessUserPartnerExistingCompanies: WritableSignal<Company[]> = signal<Company[]>([]);
  readonly submitLoading: WritableSignal<boolean> = signal<boolean>(false);

  readonly isLegal: Signal<boolean> = computed(() => this.dialogData().mode === 'Legal');
  readonly isCapitalDeposit: Signal<boolean> = computed(() => this.dialogData().mode === 'CapitalDeposit');

  readonly capacities: UserCapacity[] = DIRECTOR_CAPACITIES;
  readonly siretMask = SIRET_MASK;

  readonly state: CreateCompanyState = this.createCompanyStateFactory.create(this.dialogData().mode);

  readonly labelCategoryPlans$: Observable<LabelCategoryPlan[]> =
    this.businessUserApiService.getBusinessUserLabelCategoryPlans();
  readonly labelsPlans$: Observable<LabelsPlan[]> = FormHelper.getValueChange(
    this.state.form.companyStep.company.businessUnit
  ).pipe(
    switchMap((businessUnit: BusinessUnit) => this.getLabelsPlans(businessUnit?.id)),
    remainSubscribedOnError()
  );

  readonly mapToUserStepDescription: Mapper<boolean, string> = (
    stepIsNavigable: boolean,
    lastName: string,
    firstName: string
  ) => {
    if (!stepIsNavigable) {
      return '';
    }

    return `${lastName ?? ''} ${firstName ?? ''}`;
  };

  readonly mapToCompanyStepDescription: Mapper<boolean, string> = (stepIsNavigable: boolean, name: string) => {
    if (!stepIsNavigable) {
      return '';
    }

    return name;
  };

  readonly mapToInvitationStepDescription: Mapper<boolean, string> = (stepIsNavigable: boolean, invite: boolean) => {
    if (!stepIsNavigable) {
      return '';
    }

    if (!invite) {
      return 'Ne pas inviter sur les apps Tiime';
    }

    return `Inviter sur les apps Tiime`;
  };

  readonly mapToParametersStepDescription: Mapper<boolean, string> = (
    stepIsNavigable: boolean,
    labelMode: ParametersStepLabelModeType,
    fecFile: File,
    labelPlanId: number,
    labelsPlans: LabelsPlan[]
  ) => {
    if (!stepIsNavigable || !labelMode) {
      return '';
    }

    if (labelMode === 'fecFile' && !!fecFile) {
      return `Import fichier FEC "${fecFile.name}"`;
    }

    const labelPlan = labelsPlans.find(lP => lP.id === labelPlanId);

    if (labelMode === 'labelPlan' && !!labelPlan) {
      return `Plan de labels "${labelPlan.name}"`;
    }

    return '';
  };

  private readonly subscription: Subscription = new Subscription();

  constructor(
    dialogRef: MatDialogRef<CreateCompanyDialogComponent>,
    private readonly userApiService: UserApiService,
    private readonly companyApiService: CompanyApiService,
    private readonly accountingConfigApiService: AccountingConfigApiService,
    private readonly userInvitationApiService: UserInvitationApiService,
    private readonly snackbarService: TiimeSnackbarService,
    private readonly existingUserDialogService: ExistingUserDialogService,
    private readonly businessUserApiService: BusinessUserApiService,
    private readonly prospectApiService: ProspectApiService,
    private readonly businessUnitApiService: BusinessUnitApiService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly creationFormalityApiService: CreationFormalityApiService,
    private readonly formalitiesApiService: FormalityApiService,
    private readonly createCompanyStateFactory: CreateCompanyStateFactory,
    private readonly store: Store<AppStoreState>
  ) {
    super(dialogRef);
  }

  ngOnInit(): void {
    this.state.initForm(this.dialogData().businessUnits, this.dialogData().prospect);
  }

  ngAfterViewInit(): void {
    this.observeStepChange();
  }

  closeCreateCompanyDialog(): void {
    if (this.submitLoading()) {
      return;
    }

    this.cancel();
  }

  submitForm(): void {
    if (this.state.form.invalid) {
      this.state.form.markAllAsTouched();
      return;
    }

    this.submitLoading.set(true);
    const createCompany: CreateCompany = this.state.toCreateCompany();
    if (this.dialogData()?.prospect?.id) {
      createCompany.prospect = this.dialogData()?.prospect;
    }
    this.state.form.disable();

    this.companyApiService
      .createCompany(createCompany)
      .pipe(
        switchMap((createdCompany: Company) =>
          forkJoin([
            this.createInvitation(createdCompany),
            this.uploadFEC(createdCompany),
            this.importLabelPlan(createdCompany),
            this.validateCompany(createdCompany),
            this.updateAccountingConfig(createdCompany),
            this.createCreationFormality(createdCompany),
            this.createCapitalDepositFormality(createdCompany)
          ]).pipe(
            tap(data => {
              const creationRequest = data[5];
              if (creationRequest) {
                createdCompany.creationRequestClosed = false;
                createdCompany.creationRequestId = creationRequest.creationDetail.id;
              }

              this.closeAfterSubmit(createdCompany);
            }),
            catchError(err => {
              this.closeAfterSubmit(createdCompany);
              return throwError(() => err);
            })
          )
        ),
        finalize(() => {
          this.submitLoading.set(false);
          this.state.form.enable();
        })
      )
      .subscribe();
  }

  private closeAfterSubmit(createdCompany: Company): void {
    this.dialogRef.close(createdCompany);
    this.snackbarService.openSuccess('Le dossier a bien été ajouté');
  }

  validateUserStep(): void {
    this.setUserDoesntExist(false);
    this.resetUserExistingCompanies();

    if (!this.state.form.userStep.user.valid) {
      this.state.form.userStep.user.markAllAsTouched();
      return;
    }

    if (this.state.form.userStep.user.id.value && this.state.form.userStep.user.email.disabled) {
      this.checkUserExistingCompanies();
      return;
    }

    this.isLoading.set(true);
    const user = this.state.form.userStep.user.toUser();

    this.userApiService
      .checkIfEmailOrPhoneExist(user)
      .pipe(
        switchMap((existingUsers: User[]) => {
          const userWithSameEmail = existingUsers
            .filter((existingUser: User) => existingUser.email)
            .find(
              (existingUser: User) =>
                StringHelper.normalizeToLowerCase(existingUser.email) === StringHelper.normalizeToLowerCase(user.email)
            );

          if (userWithSameEmail) {
            return this.existingUserDialogService
              .open({ data: userWithSameEmail })
              .afterClosed()
              .pipe(
                filter((selectedUser: User) => !!selectedUser),
                tap((selectedUser: User) => this.createUserFormComponent()?.selectUserSuggestion(selectedUser))
              );
          } else {
            this.checkUserExistingCompanies();
            return of(true);
          }
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe();
  }

  validateCompanyStep(): void {
    this.setCompanyDoesntExist(false);
    this.resetBusinessUserPartnerExistingCompanies();

    if (!this.state.form.companyStep.company.valid) {
      this.state.form.companyStep.company.markAllAsTouched();
      return;
    }

    const siret: string = this.state.form.companyStep.company.siret.value;
    const isSiretNumeric: boolean = RegexHelper.siretRegex.test(this.state.form.companyStep.company.siret.value);

    if (this.isLegal()) {
      this.setCompanyDoesntExist(true);
      this.submitForm();
      return;
    }

    if (siret && isSiretNumeric) {
      this.checkExistingCompanies(StringHelper.removeSpaces(siret));
      return;
    }

    this.goToCompanyNextStep();
  }

  selectUserExistingCompany(searchUser: SearchUser): void {
    if (!this.dialogData().prospect) {
      return;
    }

    this.confirmDialogService
      .open({
        data: new ConfirmDialogData(
          'Attention',
          `Êtes-vous sûr de vouloir sélectionner le dossier ${searchUser.company.name} ?`,
          'Confirmer',
          'Annuler',
          'Le prospect ainsi que ses contrats seront définitivement liés à ce dossier',
          'accent'
        )
      })
      .afterClosed()
      .pipe(
        filter((confirm: boolean) => confirm),
        tap(() => this.submitLoading.set(true)),
        switchMap(() =>
          this.linkProspectToCompany(this.dialogData().prospect.id, searchUser.company.id, searchUser.id)
        ),
        tap(() => {
          this.dialogRef.close(searchUser.company);
          this.snackbarService.openSuccess('Le dossier a bien été ajouté');
        }),
        finalize(() => this.submitLoading.set(false))
      )
      .subscribe();
  }

  resetUserExistingCompanies(): void {
    this.setUserExistingCompanies([]);
  }

  resetBusinessUserPartnerExistingCompanies(): void {
    this.setBusinessUserPartnerExistingCompanies([]);
  }

  goToUserNextStep(): void {
    this.setUserDoesntExist(true);
    this.resetUserExistingCompanies();
    this.goToNextStep();
  }

  goToCompanyNextStep(): void {
    this.setCompanyDoesntExist(true);
    this.resetBusinessUserPartnerExistingCompanies();
    this.goToNextStep();
  }

  private checkUserExistingCompanies(): void {
    this.isLoading.set(true);

    const userEmail = this.state.form.userStep.controls.user.email.value;
    const userPhone = this.state.form.userStep.controls.user.phone.value;

    this.getUserExistingCompaniesFromBusinessUserPartner(userEmail, userPhone)
      .pipe(
        tap((searchUser: SearchUser[]) => {
          if (searchUser.length === 0) {
            this.goToUserNextStep();
            return;
          }

          this.setUserExistingCompanies(searchUser);
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe();
  }

  private checkExistingCompanies(siret: string): void {
    this.isLoading.set(true);

    const companySiren: string = siret.slice(0, 9);

    this.searchCompaniesFromBusinessUserPartner(companySiren)
      .pipe(
        map((companies: Company[]) =>
          companies.filter(
            (company: Company) =>
              StringHelper.normalizeToLowerCase(company.siret) === StringHelper.normalizeToLowerCase(siret)
          )
        ),
        tap((companies: Company[]) => {
          if (companies.length === 0) {
            this.goToCompanyNextStep();
            return;
          }

          this.setBusinessUserPartnerExistingCompanies(companies);
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe();
  }

  private getUserExistingCompaniesFromBusinessUserPartner(email: string, phone: string): Observable<SearchUser[]> {
    return this.userApiService.searchUsersFromBusinessUserPartner({ email, phone });
  }

  private searchCompaniesFromBusinessUserPartner(siren: string): Observable<Company[]> {
    return this.businessUserApiService.searchCompaniesFromBusinessUserPartner({ siren });
  }

  private observeStepChange(): void {
    this.subscription.add(
      this.tiimeStepperComponent()
        .selectionChange.pipe(
          tap((stepperSelectionEvent: StepperSelectionEvent) => {
            this.tableAnchorComponent()?.scrollTo();
            switch (stepperSelectionEvent.selectedIndex) {
              case this.state.availableSteps.findIndex(step => step === 'User'):
                this.createUserFormComponent()?.focusUserLastNameInput();
                break;
              case this.state.availableSteps.findIndex(step => step === 'Company'): {
                if (this.state.companyStepDisplayedForms.includes('siret')) {
                  this.createCompanyFormComponent()?.focusCompanySiretInput();
                } else {
                  this.createCompanyFormComponent()?.focusCompanyNameInput();
                }

                if (this.isLegal()) {
                  this.state.form.companyStep.company.name.setValue(
                    `${this.state.form.userStep.user.lastName.value} ${this.state.form.userStep.user.firstName.value} (à changer)`
                  );
                }

                break;
              }
            }
          })
        )
        .subscribe()
    );
  }

  private linkProspectToCompany(prospectId: number, companyId: number, userId: number): Observable<void> {
    return this.prospectApiService.linkToCompany(prospectId, companyId, userId);
  }

  private setUserExistingCompanies(companies: SearchUser[]): void {
    this.userExistingCompanies.set(companies);
  }

  private setBusinessUserPartnerExistingCompanies(companies: Company[]): void {
    this.businessUserPartnerExistingCompanies.set(companies);
  }

  private setUserDoesntExist(value: boolean): void {
    this.state.form.userStep.userDoesntExist.setValue(value);
  }

  private setCompanyDoesntExist(value: boolean): void {
    this.state.form.companyStep.companyDoesntExist.setValue(value);
  }

  private goToNextStep(): void {
    this.tiimeStepperComponent().next();
  }

  private createInvitation(company: Company): Observable<null | UserInvitation | UserInvitation[]> {
    if (!this.state.availableSteps.includes('Invitation')) {
      return of(null);
    }

    const invitation: UserInvitationConfiguration =
      this.state.form.invitationStep.invitation.toUserInvitationConfiguration();
    const user: User = company.users[0];

    // On peut skip cette étape  si on a pas de user ou si on n'a pas d'invitation
    if (!user?.id || (invitation.features.length === 0 && !invitation.withFormation)) {
      return of(null);
    }

    return this.userInvitationApiService
      .createUserInvitation(user.id, invitation, company.id)
      .pipe(catchError(() => of(null)));
  }

  private uploadFEC(company: Company): Observable<null | any> {
    if (!this.state.availableSteps.includes('Parameters')) {
      return of(null);
    }

    const fecFile: File = this.state.form.parametersStep.fecFile.value;

    // On peut skip cette étape si on n'a pas de fec
    if (!fecFile) {
      return of(null);
    }

    return this.companyApiService.uploadFEC(fecFile, company.id).pipe(catchError(() => of(null)));
  }

  private importLabelPlan(company: Company): Observable<null | LabelsPlan> {
    if (!this.state.availableSteps.includes('Parameters')) {
      return of(null);
    }

    const labelPlanId: number = this.state.form.parametersStep.labelPlan.value;

    // On peut skip cette étape si on n'a pas de plan de label ou si c'est le plan de label Tiime
    if (!labelPlanId || labelPlanId === TIIME_LABEL_PLAN_ID) {
      return of(null);
    }

    return this.companyApiService.importLabelsPlans(labelPlanId, company.id).pipe(catchError(() => of(null)));
  }

  private validateCompany(company: Company): Observable<null | Company> {
    const fecFile: File = this.state.form.parametersStep.fecFile.value;
    const labelPlanId: number = this.state.form.parametersStep.labelPlan.value;

    // On peut skip cette étape si on a un fec ou si on n'a pas de plan de label ou si c'est le plan de label Tiime
    if (!this.isLegal() && (fecFile || !labelPlanId || labelPlanId === TIIME_LABEL_PLAN_ID)) {
      return of(null);
    }

    return this.companyApiService.validateCompany(company.id).pipe(catchError(() => of(null)));
  }

  private createCreationFormality(company: Company): Observable<null | CreationFormality> {
    // On peut skip cette étape si le mode de création n'est pas legal
    if (!this.isLegal()) {
      return of(null);
    }

    let formalitySupervisor: FormalitySupervisorEnum = FormalitySupervisorEnum.monFormaliste;

    const isNumbr = this.store.selectSignal(businessUserPartnerIsNumbrSelector);

    if (isNumbr()) {
      formalitySupervisor = FormalitySupervisorEnum.interne;
    }

    return this.creationFormalityApiService
      .createCreationFormalityFromCompany(company.id, formalitySupervisor)
      .pipe(catchError(() => of(null)));
  }

  private createCapitalDepositFormality(company: Company): Observable<null | Formality> {
    // On peut skip cette étape si le mode de création n'est pas dépôt de capital
    if (!this.isCapitalDeposit() || !this.partnerConfig().accessCapitalDepositFormality) {
      return of(null);
    }

    return this.formalitiesApiService.postFormality(
      new FormalityBase(
        null,
        FormalityTypeEnum.exceptional,
        FormalityCodeNameEnum.capitalDeposit,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null
      ),
      company.id
    );
  }

  private updateAccountingConfig(company: Company): Observable<null | AccountingConfig> {
    const categorySetupPlanId: number = this.state.form.parametersStep.controls.categorySetupPlan.value;

    // On peut skip cette étape si on n'a pas de plan de catégorie
    if (!categorySetupPlanId) {
      return of(null);
    }

    return this.accountingConfigApiService.getAccountingConfig(company.id).pipe(
      switchMap((accountingConfig: AccountingConfig) => {
        accountingConfig.categorySetupPlan = new LabelCategoryPlan(categorySetupPlanId);
        return this.accountingConfigApiService.updateAccountingConfig(accountingConfig, company.id);
      }),
      catchError(() => of(null))
    );
  }

  private getLabelsPlans(businessUnitId: number): Observable<LabelsPlan[]> {
    const tiimeLabelPlan: LabelsPlan = new LabelsPlan(TIIME_LABEL_PLAN_ID, 'Tiime');

    if (!businessUnitId) {
      return of([tiimeLabelPlan]);
    }

    return this.businessUnitApiService
      .getLabelsPlans(businessUnitId)
      .pipe(map((labelPlans: LabelsPlan[]) => (labelPlans?.length ? labelPlans : [tiimeLabelPlan])));
  }
}
